import { searchContext } from '../../../../context/Context';
import { useMappingDrillDown } from '../../../../context/MappingDrillDownContext';
import { DATASETS } from '../../../Constants';
import updateParams from '../../../../utils/updateParams';
import { getCategoryByTab, getHeaderProps } from '../config/helpers';
import { renderMappingButton } from '../renders/buttonRenderers';
import RestrictedContent from '../../components/RestrictedContent';
import RestrictedHeader from '../../components/RestrictedHeader';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import HighZTooltip from '../../components/HighZTooltip';

const MAX_PMIDS_IN_PUBMED_URL = 150;
const PUBMED_ADVANCED_SEARCH_URL = 'https://pubmed.ncbi.nlm.nih.gov/advanced/';

function resolveGeneId(data: any): string | number | undefined {
  return (
    data?.geneID ??
    data?.ncbiID ??
    data?.geneNcbiID ??
    data?.gene_ncbi_id ??
    (Array.isArray(data?.geneNcbiIDs) ? data.geneNcbiIDs[0] : data?.geneNcbiIDs)
  );
}

function extractIcitePmids(iciteCitedBy: unknown): string[] {
  if (!Array.isArray(iciteCitedBy)) return [];

  const pmids: string[] = [];
  const stack = [...iciteCitedBy];

  while (stack.length > 0) {
    const value = stack.shift();

    if (Array.isArray(value)) {
      stack.unshift(...value);
      continue;
    }

    const normalizedValue = String(value).trim();
    if (/^\d+$/.test(normalizedValue)) {
      pmids.push(normalizedValue);
    }
  }

  return pmids;
}

function buildPubmedCitedByUrl(pmids: string[]): string | null {
  if (pmids.length === 0) return null;
  return `https://pubmed.ncbi.nlm.nih.gov/?term=${pmids.join(',')}`;
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
}

function NumberPmidsWithChemsIncludedInEvidenceCell({
  data,
  isChemicalRestricted,
}: {
  data: any;
  isChemicalRestricted: boolean;
}) {
  const { tab } = searchContext();
  const { effectiveTab } = useMappingDrillDown();
  const resolvedTab = effectiveTab || tab;

  let mappingItems: any[] = [];
  let columVisibilityModelKey = '';
  let tabSegment = '';

  if (resolvedTab === 'GDA') {
    columVisibilityModelKey = 'numberPmidsWithChemsIncludedInEvidenceGDA';
    tabSegment = 'gda';
    mappingItems = [
      {
        filterParam: 'disease',
        filterValue: data.diseaseUMLSCUI,
        name: data.diseaseName,
        id: data.diseaseUMLSCUI,
      },
      {
        filterParam: 'gene_ncbi_id',
        filterValue: data.geneNcbiID,
        name: data.geneSymbol,
        id: data.geneDescription,
      },
      {
        filterParam: 'chem_ids_filter',
        filterValue: 'ALL',
      },
    ];
  } else if (resolvedTab === 'VDA') {
    columVisibilityModelKey = 'numberPmidsWithChemsIncludedInEvidenceVDA';
    tabSegment = 'vda';
    mappingItems = [
      {
        filterParam: 'disease',
        filterValue: data.diseaseUMLSCUI,
        name: data.diseaseName,
        id: data.diseaseUMLSCUI,
      },
      {
        filterParam: 'variant',
        filterValue: data.variantStrID,
        name: data.variantStrID,
        id: data.geneSymbol,
      },
      {
        filterParam: 'chem_ids_filter',
        filterValue: 'ALL',
      },
    ];
  } else if (resolvedTab === 'GENES') {
    columVisibilityModelKey = 'numberPmidsWithChemsIncludedInEvidenceGDA';
    tabSegment = 'gda';
    mappingItems = [
      {
        filterParam: 'gene_symbol',
        filterValue: data.symbol,
        name: data.symbol,
        id: data.geneID,
      },
      {
        filterParam: 'chem_ids_filter',
        filterValue: 'ALL',
      },
    ];
  } else if (resolvedTab === 'VARIANTS') {
    columVisibilityModelKey = 'numberPmidsWithChemsIncludedInEvidenceVDA';
    tabSegment = 'vda';
    mappingItems = [
      {
        filterParam: 'variant',
        filterValue: data.strID,
        name: data.geneSymbol,
        id: data.strID,
      },
      {
        filterParam: 'chem_ids_filter',
        filterValue: 'ALL',
      },
    ];
  }

  if (!data.numberPmidsWithChemsIncludedInEvidence || data.numberPmidsWithChemsIncludedInEvidence === 0) {
    return <RestrictedContent isRestricted={isChemicalRestricted}>0</RestrictedContent>;
  }

  const config = {
    displayValue: data.numberPmidsWithChemsIncludedInEvidence,
    mappingItems,
    columVisibilityModelKey,
    tabSegment,
    name: 'numberPmidsWithChemsIncludedInEvidence',
    viewSegment: '/all',
  };

  return (
    <RestrictedContent isRestricted={isChemicalRestricted}>
      {renderMappingButton(data, config)}
    </RestrictedContent>
  );
}

function NumChemicalsColumnCell({
  data,
  isChemicalRestricted,
}: {
  data: any;
  isChemicalRestricted: boolean;
}) {
  const { tab } = searchContext();

  let mappingItems: any[] = [];
  const columVisibilityModelKey = 'numChemicalsDISEASES';
  const tabSegment = 'chem';

  if (tab === 'DISEASES') {
    mappingItems = [
      {
        filterParam: 'disease',
        filterValue: data.diseaseUMLSCUI,
        name: data.name,
        id: data.diseaseUMLSCUI,
      },
    ];
  } else if (tab === 'GENES') {
    mappingItems = [
      {
        filterParam: 'geneSymbol',
        filterValue: data.symbol,
        name: data.symbol,
        id: data.geneID,
      },
    ];
  } else if (tab === 'VARIANTS') {
    mappingItems = [
      {
        filterParam: 'variantStrID',
        filterValue: data.strID,
        name: data.geneSymbol,
        id: data.strID,
      },
    ];
  }

  if (!data.numChemicals || data.numChemicals === 0) {
    return <RestrictedContent isRestricted={isChemicalRestricted}>0</RestrictedContent>;
  }

  const config = {
    displayValue: data.numChemicals,
    mappingItems,
    columVisibilityModelKey,
    tabSegment,
    name: 'numChemicals',
    viewSegment: '/all',
  };

  return (
    <RestrictedContent isRestricted={isChemicalRestricted}>
      {renderMappingButton(data, config)}
    </RestrictedContent>
  );
}

export const getMetricsColumns = context => {
  const { tab, isGDA, isVDA, isAuthenticated, isAcademic } = context;
  const headerProps = getHeaderProps();
  const isChemicalRestricted = isAuthenticated && isAcademic;
  const categoryForTab = isGDA ? 'GDA' : isVDA ? 'VDA' : (getCategoryByTab(tab) ?? tab);

  return [
    {
      name: 'numCTs',
      category: categoryForTab,
      sortable: true,
      header: <span>N CTs</span>,
      headerProps,
      columnSize: 10,
      render: ({ data }) => {
        const { tab } = searchContext();
        const nRaw = Number(data?.numCTs);
        const n = Number.isFinite(nRaw) ? nRaw : 0;

        if (tab === 'GDA') {
          if (n === 0) return 0;
          const config = {
            displayValue: data.numCTs,
            mappingItems: [
              {
                filterParam: 'disease',
                filterValue: data.diseaseUMLSCUI,
                name: data.diseaseName,
                id: data.diseaseUMLSCUI,
              },
              {
                filterParam: 'gene_ncbi_id',
                filterValue: data.geneNcbiID,
                name: data.geneSymbol,
                id: data.geneDescription,
              },
              {
                filterParam: 'reference_type',
                filterValue: 'NCTID',
              },
              {
                filterParam: 'source',
                filterValue: 'CLINICALTRIALS',
              },
            ],
            columVisibilityModelKey: 'GENES_GDA',
            tabSegment: 'gda',
            name: 'numCTs',
          };

          return renderMappingButton(data, config);
        }

        if (tab === 'DISEASES') {
          const newParams = updateParams({
            view: DATASETS.DISEASES,
            tab: 'GDA',
            searchItems: [
              {
                id: data.diseaseUMLSCUI,
                name: data.diseaseName,
                filterParam: 'disease',
                filterValue: data.diseaseUMLSCUI,
              },
            ],
            source: 'CLINICALTRIALS',
          });

          const nRaw = Number(data?.numCTs);
          const n = Number.isFinite(nRaw) ? nRaw : 0;
          if (n === 0) return 0;

          return (
            <a className="tab-links" href={`?${newParams}`}>
              {data.numCTs}
            </a>
          );
        }

        if (tab === 'GENES') {
          const nRaw = Number(data?.numCTs);
          const n = Number.isFinite(nRaw) ? nRaw : 0;
          if (n === 0) return 0;

          const geneId = resolveGeneId(data);
          const newParams = updateParams({
            view: DATASETS.GENES,
            tab: 'GDA',
            searchItems: [{ id: String(geneId) }],
            source: 'CLINICALTRIALS',
          });

          return (
            <a className="tab-links" href={`?${newParams}`}>
              {data.numCTs}
            </a>
          );
        }

        if (tab === 'CHEMICALS') {
          const newParams = updateParams({
            view: DATASETS.CHEMICALS,
            tab: 'GDA',
            searchItems: [{ id: data.chemID }],
            source: 'CLINICALTRIALS',
          });

          return (
            <a className="tab-links" href={`?${newParams}`}>
              {data.numPublications_ALL_NCTs}
            </a>
          );
        }

        return data.numPublications_ALL_NCTs;
      },
    },

    {
      name: 'citation_count',
      category: isGDA ? 'GDA' : 'VDA',
      sortable: true,
      header: <span title="">Citation Count</span>,
      headerProps,
      minWidth: 160,
      defaultFlex: 1,
      render: ({ data }) => {
        const citationCount = data?.citation_count;
        const pmids = extractIcitePmids(data?.icite_cited_by);
        const pubmedUrl =
          pmids.length > 0 && pmids.length <= MAX_PMIDS_IN_PUBMED_URL ? buildPubmedCitedByUrl(pmids) : null;

        if (citationCount === null || citationCount === undefined) {
          return '-';
        }

        if (pmids.length === 0) {
          return citationCount;
        }

        if (pubmedUrl) {
          return (
            <HighZTooltip
              content={`Open PubMed with ${pmids.length} PMID${pmids.length > 1 ? 's' : ''}`}
              position="bottom"
            >
              <a
                className="tab-links text-decoration-none"
                href={pubmedUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {citationCount} <i className="fa fa-external-link" aria-hidden="true"></i>
              </a>
            </HighZTooltip>
          );
        }

        return (
          <HighZTooltip
            content="For several PMIDs, copy all and open PubMed Advanced Search"
            position="bottom"
          >
            <button
              type="button"
              className="tab-links text-decoration-none border-0 bg-transparent p-0 flex items-center"
              onClick={() => {
                void (async () => {
                  await copyTextToClipboard(pmids.join(','));
                  window.open(PUBMED_ADVANCED_SEARCH_URL, '_blank', 'noopener,noreferrer');
                })();
              }}
            >
              {citationCount} <ClipboardDocumentIcon className="w-4 h-4 ml-1" />
              <i className="fa fa-external-link" aria-hidden="true"></i>
            </button>
          </HighZTooltip>
        );
      },
    },

    {
      name: 'numChemicals',
      category: categoryForTab,
      sortable: !isChemicalRestricted,
      minWidth: 115,
      header: (
        <RestrictedHeader isRestricted={isChemicalRestricted}>
          <span title="">N Chemicals</span>
        </RestrictedHeader>
      ),
      headerProps,
      render: ({ data }) => (
        <NumChemicalsColumnCell data={data} isChemicalRestricted={isChemicalRestricted} />
      ),
    },

    {
      name: 'sourceScore',
      category: isGDA ? 'GDA' : 'VDA',
      sortable: true,
      header: <span title="">My Score</span>,
      headerProps,
      minWidth: 160,
      defaultFlex: 1,
      render: ({ data }) => {
        if (data.sourceScore !== null && data.sourceScore !== undefined) {
          return data.sourceScore.toFixed(2);
        }
        return null;
      },
    },

    {
      name: 'numberPmidsWithChemsIncludedInEvidence',
      category: categoryForTab,
      sortable: !isChemicalRestricted,
      header: (
        <RestrictedHeader isRestricted={isChemicalRestricted}>
          <span title="Number of Pmids With Chems Included in the Evidences">N PMIDs Chemicals</span>
        </RestrictedHeader>
      ),
      headerProps,
      minWidth: 180,
      render: ({ data }) => (
        <NumberPmidsWithChemsIncludedInEvidenceCell data={data} isChemicalRestricted={isChemicalRestricted} />
      ),
    },

    {
      name: 'numberNCTSWithChemsIncludedInEvidence',
      category: 'GDA',
      sortable: !isChemicalRestricted,
      header: (
        <RestrictedHeader isRestricted={isChemicalRestricted}>
          <span title="Number of Clinical Trial With Chems Included in the Evidences">N CTs Chemicals</span>
        </RestrictedHeader>
      ),
      headerProps,
      minWidth: 180,
      render: ({ data }) => {
        if (!data.numberNCTSWithChemsIncludedInEvidence || data.numberNCTSWithChemsIncludedInEvidence === 0) {
          return <RestrictedContent isRestricted={isChemicalRestricted}>0</RestrictedContent>;
        }

        const config = {
          displayValue: data.numberNCTSWithChemsIncludedInEvidence,
          mappingItems: [
            {
              filterParam: 'disease',
              filterValue: data.diseaseUMLSCUI,
              name: data.diseaseName,
              id: data.diseaseUMLSCUI,
            },
            {
              filterParam: 'gene_ncbi_id',
              filterValue: data.geneNcbiID,
              name: data.geneSymbol,
              id: data.geneDescription,
            },
            {
              filterParam: 'chem_ids_filter',
              filterValue: 'ALL',
            },
          ],
          columVisibilityModelKey: 'numberPmidsWithChemsIncludedInEvidenceGDA',
          tabSegment: 'gda',
          name: 'numberNCTSWithChemsIncludedInEvidence',
          viewSegment: '/all', // N Chemicals debe usar /all
        };

        return (
          <RestrictedContent isRestricted={isChemicalRestricted}>
            {renderMappingButton(data, config)}
          </RestrictedContent>
        );
      },
    },

    {
      name: 'chemNumPublicationsGDA',
      category: 'GDA',
      sortable: true,
      header: (
        <span title="Number of Publications mentioning this disease-gene association containing the chemical">
          N PMIDs this GDA
        </span>
      ),
      headerProps,
      minWidth: 195,
      render: ({ data }) => {
        const config = {
          displayValue: data.chemNumPublicationsGDA,
          mappingItems: [
            {
              filterParam: 'disease',
              filterValue: data.diseaseUMLSCUI,
              name: data.diseaseName,
              id: data.diseaseUMLSCUI,
            },
            {
              filterParam: 'gene_ncbi_id',
              filterValue: data.geneNcbiID,
              name: data.geneSymbol,
              id: data.geneDescription,
            },
            {
              filterParam: 'chem_ids_filter',
              filterValue: data.chemID,
              name: data.chemPrefName,
              id: data.chemID,
            },
          ],
          columVisibilityModelKey: 'GDA_ALL',
          tabSegment: 'gda',
          name: 'chemNumPublicationsGDA',
          viewSegment: '/all', // N Chemicals debe usar /all
        };

        return renderMappingButton(data, config);
      },
    },

    {
      name: 'chemNumPublicationsVDA',
      category: 'VDA',
      sortable: true,
      header: (
        <span title="Number of Publications mentioning this disease-variant association containing the chemical">
          N PMIDs this VDA
        </span>
      ),
      headerProps,
      minWidth: 195,
      render: ({ data }) => {
        const config = {
          displayValue: data.chemNumPublicationsVDA,
          mappingItems: [
            {
              filterParam: 'disease',
              filterValue: data.diseaseUMLSCUI,
              name: data.diseaseName,
              id: data.diseaseUMLSCUI,
            },
            {
              filterParam: 'variant',
              filterValue: data.variantStrID,
              name: data.variantStrID,
              id: data.geneSymbol,
            },
            {
              filterParam: 'chem_ids_filter',
              filterValue: data.chemID,
              name: data.chemPrefName,
              id: data.chemID,
            },
          ],
          columVisibilityModelKey: 'VDA_ALL',
          tabSegment: 'vda',
          name: 'chemNumPublicationsVDA',
          viewSegment: '/all', // N Chemicals debe usar /all
        };

        return renderMappingButton(data, config);
      },
    },
  ];
};
